import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { useToast } from "@/components/Toast";
import { useApi } from "@/lib/api";
import { capitalizeFirst } from "@/lib/capitalizeFirst";

interface HasNameId {
  id: string;
  name: string;
}

interface UseEntitySettingsOptions<TEntity> {
  entityId: string | undefined;
  endpointSegment: string;
  entityLabel: string;
  buildDeleteConfirmMessage: (name: string) => string;
  buildDeleteConfirmTitle?: (name: string) => string;
  deleteSuccessMessage?: (name: string) => string;
  afterDeleteNavigateTo: string;
  onRenamed?: (name: string) => void;
  onLoaded?: (entity: TEntity) => void;
  buildPatchBody?: (name: string) => unknown;
  saveSuccessMessage?: string;
  saveFailureMessage?: string;
}

export function useEntitySettings<TEntity extends HasNameId>({
  entityId,
  endpointSegment,
  entityLabel,
  buildDeleteConfirmMessage,
  buildDeleteConfirmTitle,
  deleteSuccessMessage,
  afterDeleteNavigateTo,
  onRenamed,
  onLoaded,
  buildPatchBody,
  saveSuccessMessage,
  saveFailureMessage,
}: UseEntitySettingsOptions<TEntity>) {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [entity, setEntity] = useState<TEntity | null>(null);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast, showToast } = useToast();
  const { confirm, dialog: confirmDialog } = useConfirm();

  const fetchEntity = useCallback(async () => {
    if (!accessToken || !orgId || !entityId) {
      return null;
    }

    return api.get<TEntity>(
      `/organizations/${orgId}/${endpointSegment}/${entityId}`,
    );
  }, [accessToken, api, orgId, endpointSegment, entityId]);

  const onLoadedRef = useRef(onLoaded);

  useEffect(() => {
    onLoadedRef.current = onLoaded;
  });

  const applyEntity = useCallback(
    (data: TEntity | null) => {
      if (data) {
        setEntity(data);
        setName(data.name);
        setError(null);
        onLoadedRef.current?.(data);
      } else {
        setError(`Couldn't load ${entityLabel}.`);
      }
    },
    [entityLabel],
  );

  useEffect(() => {
    let cancelled = false;

    void fetchEntity().then((data) => {
      if (!cancelled) {
        applyEntity(data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchEntity, applyEntity]);

  const onSaveFailed = saveFailureMessage ?? `Couldn't rename ${entityLabel}.`;

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();

    if (!accessToken || !orgId || !entityId) {
      return;
    }

    setSubmitting(true);

    try {
      await api.send(
        `/organizations/${orgId}/${endpointSegment}/${entityId}`,
        "PATCH",
        buildPatchBody ? buildPatchBody(name) : { name },
        onSaveFailed,
      );

      showToast(
        saveSuccessMessage ?? `${capitalizeFirst(entityLabel)} renamed.`,
        "success",
      );
      onRenamed?.(name);
      applyEntity(await fetchEntity());
    } catch (err) {
      showToast(err instanceof Error ? err.message : onSaveFailed, "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!accessToken || !orgId || !entityId || !entity) {
      return;
    }

    const confirmed = await confirm(buildDeleteConfirmMessage(entity.name), {
      title: buildDeleteConfirmTitle
        ? buildDeleteConfirmTitle(entity.name)
        : `Delete ${entityLabel}`,
      confirmLabel: "Delete",
    });

    if (!confirmed) {
      return;
    }

    setSubmitting(true);

    try {
      await api.send(
        `/organizations/${orgId}/${endpointSegment}/${entityId}`,
        "DELETE",
        undefined,
        `Couldn't delete ${entityLabel}.`,
      );

      showToast(
        deleteSuccessMessage
          ? deleteSuccessMessage(entity.name)
          : `${capitalizeFirst(entityLabel)} deleted.`,
        "success",
      );
      navigate(afterDeleteNavigateTo);
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : `Couldn't delete ${entityLabel}.`,
        "error",
      );
      setSubmitting(false);
    }
  }

  return {
    entity,
    error,
    name,
    setName,
    submitting,
    toast,
    showToast,
    confirmDialog,
    handleSave,
    handleDelete,
    refresh: async () => applyEntity(await fetchEntity()),
  };
}
